<script setup lang="ts">
import { conditionSourceKey, type ConditionalRule } from "../../../lib/conditionalRules";
import type {
  NavigationPageItem,
  NavigationPageItemVariant,
  NavigationPageScale,
} from "../../../types/blocks/navigation/NavigationPageOption";
import {
  editorDisplayStates,
  editorLayers,
  findDisplayState,
  resolveLayers,
  variantStateId,
} from "./editorResolve";
import {
  NAV_FIELDS,
  formatValue,
  getField,
  isSet,
  seedFieldValue,
  setField,
  unsetField,
  type FieldDef,
} from "./fieldRegistry";
import FieldSourceTable from "./FieldSourceTable.vue";
import LayerCard from "./LayerCard.vue";
import LayerEditRow from "./LayerEditRow.vue";
import LayerFieldRow from "./LayerFieldRow.vue";
import NavigationVariantConditionsDialog from "./NavigationVariantConditionsDialog.vue";
import ResolvedCardPreview from "./ResolvedCardPreview.vue";
import SetFieldMenu from "./SetFieldMenu.vue";

const props = defineProps<{
  blockId: string;
  itemIndex: number;
  variantIndex: number;
  open: boolean;
}>();

const emit = defineEmits<{
  "update:open": [value: boolean];
  back: [];
}>();

const { t } = useI18n({ useScope: "local" });
const { block, field, patch } = useInlineBlockEditor(props.blockId);

const dialogOpen = computed({
  get: () => props.open,
  set: (value: boolean) => emit("update:open", value),
});

const scale = field<NavigationPageScale>("scale", "md");
const items = computed<NavigationPageItem[]>(() =>
  Array.isArray(block.value?.items) ? block.value.items : [],
);
const item = computed(() => items.value[props.itemIndex]);
const variants = computed<NavigationPageItemVariant[]>(() =>
  Array.isArray(item.value?.variants) ? item.value.variants : [],
);
const displayStates = computed(() => editorDisplayStates(block.value?.displayStates));
const defaultDisplayStateId = computed(
  () => displayStates.value.find((state) => state.id === "default")?.id
    ?? displayStates.value[0]?.id
    ?? "default",
);
const currentVariantId = shallowRef<string | null>(null);
const simulateMatched = shallowRef(true);
const conditionsOpen = shallowRef(false);

const currentVariantIndex = computed(() =>
  variants.value.findIndex((variant) => variant.id === currentVariantId.value),
);
const variant = computed(() =>
  currentVariantIndex.value >= 0 ? variants.value[currentVariantIndex.value] : null,
);
const allLayers = computed(() =>
  item.value && variant.value
    ? editorLayers(item.value, displayStates.value, variant.value)
    : [],
);
const appliedLayers = computed(() =>
  simulateMatched.value ? allLayers.value : allLayers.value.slice(0, 2),
);
const resolvedState = computed(() => resolveLayers(appliedLayers.value));
const baseState = computed(() =>
  item.value ? findDisplayState(displayStates.value, item.value.displayStateId) : null,
);
const matchedState = computed(() =>
  variant.value ? findDisplayState(displayStates.value, variantStateId(variant.value)) : null,
);
const baseFields = computed(() =>
  baseState.value
    ? NAV_FIELDS
      .filter((field) => isSet(getField(baseState.value, field)))
      .map((field) => ({
        key: field.key,
        label: field.label,
        value: formatValue(field, getField(baseState.value, field)),
        mono: field.kind === "mono" || field.kind === "path" || field.kind === "image",
        overriddenBy: overriddenBy(field, "base"),
      }))
    : [],
);
const itemFields = computed(() =>
  item.value
    ? NAV_FIELDS
      .filter((field) => isSet(getField(item.value, field)))
      .map((field) => ({
        key: field.key,
        label: field.label,
        value: formatValue(field, getField(item.value, field)),
        mono: field.kind === "mono" || field.kind === "path" || field.kind === "image",
        overriddenBy: overriddenBy(field, "link"),
      }))
    : [],
);
const matchedStateFields = computed(() =>
  matchedState.value
    ? NAV_FIELDS
      .filter((field) => isSet(getField(matchedState.value, field)))
      .map((field) => ({
        key: field.key,
        label: field.label,
        value: formatValue(field, getField(matchedState.value, field)),
        mono: field.kind === "mono" || field.kind === "path" || field.kind === "image",
        overriddenBy: overriddenBy(field, "state"),
      }))
    : [],
);
const variantSetFields = computed(() =>
  variant.value
    ? NAV_FIELDS.filter((field) => isSet(getField(variant.value, field)))
    : [],
);
const variantUnsetFields = computed(() =>
  variant.value
    ? NAV_FIELDS.filter((field) => !isSet(getField(variant.value, field)))
    : [],
);
const sourceRows = computed(() =>
  NAV_FIELDS
    .filter((field) => isSet(getField(resolvedState.value.resolved, field)))
    .map((field) => ({
      label: field.label,
      value: formatValue(field, getField(resolvedState.value.resolved, field)),
      mono: field.kind === "mono" || field.kind === "path" || field.kind === "image",
      source: sourceLabel(resolvedState.value.winner[field.key]?.key),
      sourceTone: resolvedState.value.winner[field.key]?.key === "base"
        ? "base"
        : resolvedState.value.winner[field.key]?.key === "state"
          ? "state"
          : resolvedState.value.winner[field.key]?.key === "variant"
            ? "variant"
            : "link",
    })),
);

watch(
  [() => props.open, () => props.variantIndex],
  ([open]) => {
    if (!open) return;
    currentVariantId.value = variants.value[props.variantIndex]?.id ?? null;
    simulateMatched.value = true;
  },
  { immediate: true },
);

watch(
  variant,
  (value) => {
    if (!value && props.open) emit("back");
  },
);

function cloneVariant(entry: NavigationPageItemVariant): NavigationPageItemVariant {
  return {
    ...entry,
    conditions: [...(entry.conditions ?? [])],
    classes: entry.classes ? { ...entry.classes } : undefined,
  };
}

function cloneItem(entry: NavigationPageItem): NavigationPageItem {
  return {
    ...entry,
    classes: entry.classes ? { ...entry.classes } : undefined,
    variants: Array.isArray(entry.variants) ? entry.variants.map(cloneVariant) : [],
  };
}

function updateCurrentItem(
  transform: (draft: NavigationPageItem) => NavigationPageItem,
) {
  const current = item.value;
  if (!current) return;

  patch({
    items: items.value.map((entry, index) =>
      index === props.itemIndex ? transform(cloneItem(entry)) : entry),
  });
}

function updateCurrentVariant(
  transform: (draft: NavigationPageItemVariant) => NavigationPageItemVariant,
) {
  const variantId = currentVariantId.value;
  if (!variantId) return;

  updateCurrentItem((draft) => ({
    ...draft,
    variants: (draft.variants ?? []).map((entry) =>
      entry.id === variantId ? transform(cloneVariant(entry)) : entry),
  }));
}

function sourceLabel(key: "base" | "link" | "state" | "variant" | undefined) {
  if (key === "base") return "Base state";
  if (key === "state") return "State";
  if (key === "variant") return "Variant";
  return "Item";
}

function overriddenBy(def: FieldDef, layerKey: "base" | "link" | "state") {
  const winner = resolvedState.value.winner[def.key];
  if (!winner || winner.key === layerKey) return null;
  return `overridden by ${sourceLabel(winner.key).toLowerCase()}`;
}

function writeField(def: FieldDef, value: unknown) {
  updateCurrentVariant((draft) => setField(draft, def, value));
}

function removeField(def: FieldDef) {
  updateCurrentVariant((draft) => unsetField(draft, def));
}

function setDisplayState(value: unknown) {
  updateCurrentVariant((draft) => ({
    ...draft,
    displayStateId: String(value || defaultDisplayStateId.value),
    preset: undefined,
  }));
}

function setComposition(value: unknown) {
  updateCurrentVariant((draft) => ({
    ...draft,
    composition: value === "any" ? "any" : "all",
  }));
}

function setVariantName(value: unknown) {
  updateCurrentVariant((draft) => ({
    ...draft,
    name: String(value ?? ""),
  }));
}

function moveVariant(offset: -1 | 1) {
  const from = currentVariantIndex.value;
  const to = from + offset;
  if (from < 0 || to < 0 || to >= variants.value.length) return;

  updateCurrentItem((draft) => {
    const nextVariants = [...(draft.variants ?? [])];
    const [moved] = nextVariants.splice(from, 1);
    nextVariants.splice(to, 0, moved);
    return {
      ...draft,
      variants: nextVariants,
    };
  });
}

function deleteVariant() {
  const variantId = currentVariantId.value;
  if (!variantId) return;

  updateCurrentItem((draft) => ({
    ...draft,
    variants: (draft.variants ?? []).filter((entry) => entry.id !== variantId),
  }));
  emit("back");
}

function saveConditions(conditions: ConditionalRule[]) {
  updateCurrentVariant((draft) => ({
    ...draft,
    conditions,
  }));
}

function seedField(def: FieldDef) {
  writeField(def, seedFieldValue(def, getField(resolvedState.value.resolved, def)));
}

function conditionOperator(condition: string | undefined) {
  if (condition === "Equals") return "=";
  if (condition === "Not Equals") return "≠";
  if (condition === "Contains") return "∋";
  if (condition === "Does not contain") return "∌";
  if (condition === "Greater than") return ">";
  if (condition === "Greater than or equal") return "≥";
  if (condition === "Less than") return "<";
  if (condition === "Less than or equal") return "≤";
  return condition || "=";
}

function conditionSource(rule: ConditionalRule) {
  return rule.source ? conditionSourceKey(rule.source) : t("conditions.unknown");
}

function conditionValue(rule: ConditionalRule) {
  if (typeof rule.value === "string") return rule.value || '""';
  return JSON.stringify(rule.value);
}
</script>

<template>
  <Dialog v-model:open="dialogOpen">
    <DialogContent
      v-if="item && variant"
      class="grid-rows-[auto_minmax(0,1fr)_auto] max-h-[88dvh] gap-0 p-0 sm:max-w-[1150px]"
    >
      <DialogHeader class="border-b px-6 py-3.5 text-left">
        <DialogTitle class="space-y-2">
          <div class="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              class="size-[30px] rounded-sm"
              @click="emit('back')"
            >
              <Icon mode="svg" size="14" name="lucide:chevron-left" />
            </Button>
            <div class="text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--cat-navigation)]">
              {{ t("eyebrow", { index: currentVariantIndex + 1, count: variants.length }) }}
            </div>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <Input
              :model-value="variant.name"
              class="h-8 w-[280px] rounded-sm bg-background text-[13.5px] font-semibold"
              :placeholder="t('namePlaceholder')"
              @update:model-value="setVariantName"
            />
            <p class="text-[11.5px] font-normal text-muted-foreground">
              {{ t("caption") }}
            </p>
            <div class="ml-auto flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                class="size-8"
                :disabled="currentVariantIndex <= 0"
                @click="moveVariant(-1)"
              >
                <Icon mode="svg" size="14" name="lucide:arrow-up" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                class="size-8"
                :disabled="currentVariantIndex >= variants.length - 1"
                @click="moveVariant(1)"
              >
                <Icon mode="svg" size="14" name="lucide:arrow-down" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                class="size-8 text-destructive"
                @click="deleteVariant"
              >
                <Icon mode="svg" size="14" name="lucide:trash-2" />
              </Button>
            </div>
          </div>
        </DialogTitle>
        <DialogDescription class="sr-only">
          {{ t("description") }}
        </DialogDescription>
      </DialogHeader>

      <div class="grid min-h-0 grid-cols-[minmax(0,1fr)_396px]">
        <div class="min-h-0 overflow-y-auto px-6 py-4">
          <div class="space-y-3">
            <LayerCard
              icon="lucide:git-branch"
              :title="t('conditions.title')"
              badge="WHEN TRUE"
              badge-tone="variant"
              :subtitle="t('conditions.subtitle')"
            >
              <template #headerRight>
                <Select
                  :model-value="variant.composition"
                  @update:model-value="setComposition"
                >
                  <SelectTrigger class="h-7 w-[150px] rounded-sm bg-background text-[12px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{{ t("conditions.all") }}</SelectItem>
                    <SelectItem value="any">{{ t("conditions.any") }}</SelectItem>
                  </SelectContent>
                </Select>
              </template>

              <div v-if="variant.conditions?.length" class="space-y-2">
                <div
                  v-for="(rule, index) in variant.conditions"
                  :key="rule.id || index"
                  class="flex flex-wrap items-center gap-1.5 rounded-md border bg-background px-2 py-2 text-[11px]"
                >
                  <code class="rounded bg-secondary px-1.5 py-0.5 font-mono text-[10.5px]">
                    {{ conditionSource(rule) }}
                  </code>
                  <span class="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-semibold">
                    {{ conditionOperator(rule.condition) }}
                  </span>
                  <code class="rounded bg-[color-mix(in_oklch,var(--cat-navigation)_13%,transparent)] px-1.5 py-0.5 font-mono text-[10.5px]">
                    {{ conditionValue(rule) }}
                  </code>
                </div>
              </div>
              <p v-else class="text-[11px] text-muted-foreground">
                {{ t("conditions.empty") }}
              </p>
              <Button
                type="button"
                variant="outline"
                class="h-8 justify-start rounded-sm text-[12.5px]"
                @click="conditionsOpen = true"
              >
                <Icon mode="svg" size="14" name="lucide:pencil" class="mr-2" />
                {{ t("conditions.edit") }}
              </Button>
            </LayerCard>

            <LayerCard
              icon="lucide:swatch-book"
              :title="baseState?.name || t('layers.base')"
              badge="BASE"
              badge-tone="base"
              :subtitle="t('layers.baseSub')"
            >
              <LayerFieldRow
                v-for="field in baseFields"
                :key="field.key"
                :label="field.label"
                :value="field.value"
                :mono="field.mono"
                :overridden-by="field.overriddenBy"
              />
            </LayerCard>

            <LayerCard
              icon="lucide:pencil"
              :title="item.title || t('layers.item')"
              badge="CONTENT"
              badge-tone="link"
              :indent="1"
              :subtitle="t('layers.itemSub')"
            >
              <LayerFieldRow
                v-for="field in itemFields"
                :key="field.key"
                :label="field.label"
                :value="field.value"
                :mono="field.mono"
                :overridden-by="field.overriddenBy"
              />
            </LayerCard>

            <LayerCard
              icon="lucide:swatch-book"
              :title="matchedState?.name || t('layers.matched')"
              badge="WHEN MATCHED"
              badge-tone="state"
              :indent="2"
              :subtitle="t('layers.matchedSub')"
              :dimmed="!simulateMatched"
            >
              <template #headerRight>
                <Select
                  :model-value="variantStateId(variant)"
                  @update:model-value="setDisplayState"
                >
                  <SelectTrigger class="h-7 w-[150px] rounded-sm bg-background text-[12px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem
                      v-for="state in displayStates"
                      :key="state.id"
                      :value="state.id"
                    >
                      {{ state.name }}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </template>

              <LayerFieldRow
                v-for="field in matchedStateFields"
                :key="field.key"
                :label="field.label"
                :value="field.value"
                :mono="field.mono"
                :overridden-by="field.overriddenBy"
              />
            </LayerCard>

            <LayerCard
              icon="lucide:wand-sparkles"
              :title="t('layers.overrides')"
              badge="FINAL SAY"
              badge-tone="variant"
              :indent="3"
              :subtitle="t('layers.overridesSub')"
              :dimmed="!simulateMatched"
            >
              <LayerEditRow
                v-for="def in variantSetFields"
                :key="def.key"
                :def="def"
                :model-value="getField(variant, def)"
                @update:model-value="(value) => writeField(def, value)"
                @unset="removeField(def)"
              />
              <p v-if="!variantSetFields.length" class="text-[11px] text-muted-foreground">
                {{ t("layers.variantEmpty") }}
              </p>
              <SetFieldMenu
                tone="variant"
                :available="variantUnsetFields"
                :label="t('layers.addOverride')"
                @pick="seedField"
              />
            </LayerCard>
          </div>
        </div>

        <div class="min-h-0 overflow-y-auto border-l bg-[var(--canvas)] px-5 py-4">
          <div class="space-y-4">
            <div class="space-y-2">
              <div class="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                {{ t("simulator.label") }}
              </div>
              <div class="inline-flex rounded-md border bg-background p-0.5">
                <button
                  type="button"
                  class="rounded-[5px] px-3 py-1.5 text-[11px] font-semibold transition-colors"
                  :class="simulateMatched ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'"
                  @click="simulateMatched = true"
                >
                  {{ t("simulator.matched") }}
                </button>
                <button
                  type="button"
                  class="rounded-[5px] px-3 py-1.5 text-[11px] font-semibold transition-colors"
                  :class="!simulateMatched ? 'bg-secondary text-secondary-foreground' : 'text-muted-foreground'"
                  @click="simulateMatched = false"
                >
                  {{ t("simulator.notMatched") }}
                </button>
              </div>
              <p class="text-[11px] font-semibold" :class="simulateMatched ? 'text-success' : 'text-muted-foreground'">
                {{ simulateMatched ? t("simulator.active") : t("simulator.inactive") }}
              </p>
            </div>

            <ResolvedCardPreview :resolved="resolvedState.resolved" :scale="scale" />
            <FieldSourceTable :rows="sourceRows" />
          </div>
        </div>
      </div>

      <DialogFooter class="items-center border-t px-6 py-4 sm:justify-between">
        <p class="text-[11px] text-muted-foreground">
          {{ t("footer") }}
        </p>
        <div class="flex gap-2">
          <Button type="button" variant="ghost" @click="dialogOpen = false">{{ t("close") }}</Button>
          <Button type="button" @click="dialogOpen = false">{{ t("save") }}</Button>
        </div>
      </DialogFooter>

      <NavigationVariantConditionsDialog
        v-model:open="conditionsOpen"
        :block-id="blockId"
        :variant-id="variant.id"
        :conditions="variant.conditions ?? []"
        :show-trigger="false"
        @save="saveConditions"
      />
    </DialogContent>
  </Dialog>
</template>

<i18n lang="yaml">
en:
  eyebrow: "NAVIGATION PAGE · VARIANT {index} OF {count}"
  namePlaceholder: "Variant name"
  caption: "A variant re-styles this card while its conditions are true. Layers apply top to bottom — later wins."
  description: "Edit a navigation variant, preview matched and unmatched states, and manage conditions."
  conditions:
    title: "When does this apply?"
    subtitle: "Choose whether all or any conditions must match."
    all: "All conditions"
    any: "Any condition"
    empty: "No conditions yet — this variant will never match until you add one."
    edit: "Edit conditions"
    unknown: "Unknown source"
  layers:
    base: "Base state"
    baseSub: "These shared defaults always apply first."
    item: "Item"
    itemSub: "The link's own content sits on top of the base state."
    matched: "Matched state"
    matchedSub: "These fields apply only while the variant matches."
    overrides: "Variant overrides"
    overridesSub: "Variant overrides beat every earlier layer."
    variantEmpty: "No overrides yet — the matched display state does all the work."
    addOverride: "Override another field"
  simulator:
    label: "Preview"
    matched: "Matched"
    notMatched: "Not matched"
    active: "✓ This variant is active — its two layers apply."
    inactive: "✕ Conditions are false — only the base layers apply."
  footer: "First matching variant wins — rows apply top to bottom. Edits apply live."
  close: "Close"
  save: "Save variant"
</i18n>
